import { NgStyle } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faPlus, faSpinner, faTrash, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import {
    BodyPartInjuredOptions,
    EmployeeInjuryReport,
    EmployeeInjuryReportWitness,
    FeatureType,
    InjuredPersonType,
    ListItem,
    MechanismOfInjuryOptions,
    NatureOfInjuryOptions,
    RepositoryModal,
} from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { EmployeeInjuryReportsComponent } from './employee-injury-reports.component'

@Component({
    templateUrl: './employee-injury-report.component.html',
    styleUrls: ['./safety-checklist-modal.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle, VoiceTextFieldComponent],
})
export class EmployeeInjuryReportComponent extends RepositoryModal<EmployeeInjuryReport> {
    faX = faX
    faPlus = faPlus
    faTrash = faTrash
    faSpinner = faSpinner

    jobList: ListItem<string>[] = []
    employeeList: ListItem<number>[] = []
    injuredPersonTypeList: ListItem<string>[] = [
        new ListItem<string>(InjuredPersonType.Visitor, 'Visitor'),
        new ListItem<string>(InjuredPersonType.Temp, 'Temp'),
        new ListItem<string>(InjuredPersonType.Permanent, 'Permanent Employee'),
    ]

    natureOptions = NatureOfInjuryOptions
    mechanismOptions = MechanismOfInjuryOptions
    bodyPartOptions = BodyPartInjuredOptions

    attachments: any[] = []
    progress = 0

    protected onInitialized(): void {
        const parent = this.editOptions.initializeParams as EmployeeInjuryReportsComponent
        this.jobList = [...(parent.activeJobList ?? [])]
        this.employeeList = [...(parent.activeEmployeeList ?? [])]

        const currentJob = this.target.job
        if (currentJob && !this.jobList.some(j => j.value === currentJob)) {
            this.jobList = [new ListItem(currentJob, parent.jobDisplayForItem(currentJob) || currentJob), ...this.jobList]
        }

        this.target.witnesses = this.target.witnesses ?? []
        this.ensureEmployeeOption(this.target.injuredEmployeeHrRef, parent)
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyChecklistEdit])
    }

    addWitness() {
        const row = new EmployeeInjuryReportWitness()
        row.id = crypto.randomUUID()
        row.sortOrder = this.target.witnesses.length
        this.target.witnesses.push(row)
        this.target.witnessToInjury = true
    }

    removeWitness(index: number) {
        this.target.witnesses.splice(index, 1)
        this.target.witnesses.forEach((row, i) => (row.sortOrder = i))
        if (this.target.witnesses.length === 0) {
            this.target.witnessToInjury = false
        }
    }

    isSelected(stored: string | null | undefined, value: string): boolean {
        return (stored ?? '')
            .split('|')
            .filter(Boolean)
            .includes(value)
    }

    toggleSelected(field: 'natureOfInjury' | 'mechanismOfInjury' | 'bodyPartsInjured', value: string) {
        const current = new Set(
            (this.target[field] ?? '')
                .split('|')
                .filter(Boolean),
        )
        if (current.has(value)) {
            current.delete(value)
        } else {
            current.add(value)
        }
        this.target[field] = [...current].join('|')
    }

    removeAttachment(file: any) {
        this.attachments.remove(file)
    }

    async onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files) return

        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of Array.from(files)) {
            if (file.type.startsWith('image/')) {
                const options: Options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    onProgress: progress,
                }
                const compressedFile = await imageCompression(file, options)
                compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
                this.attachments.push(compressedFile)
            } else {
                const docFile = file as any
                docFile.url = './Resources/pdfImage.png'
                this.attachments.push(docFile)
            }
        }

        input.value = ''
    }

    isDocument(url: string): boolean {
        const lower = url.toLowerCase()
        return ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'].some(ext => lower.endsWith(ext))
    }

    getExistingFileSrc(url: string): string {
        if (this.isDocument(url)) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/SafetyChecklist/' + url + '?w=300'
    }

    getPendingFileSrc(file: any): string {
        if (file.type === 'application/pdf' || (file.name && this.isDocument(file.name))) {
            return './Resources/pdfImage.png'
        }
        return file.url
    }

    displayFileName(index: number): string {
        return this.target.imageFileNames?.[index] ?? 'Attachment'
    }

    openExistingFile(url: string) {
        window.open('./Resources/Images/SafetyChecklist/' + url, '_blank')
    }

    private uploadAttachments(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.attachments.length; i++) {
            uploadData.append('myFile' + i, this.attachments[i], this.attachments[i].name)
        }
        return this.services.data.post('api/EmployeeInjuryReport/UploadImages', uploadData)
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.attachments.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadAttachments(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }

    private ensureEmployeeOption(hrRef: number | null | undefined, parent: EmployeeInjuryReportsComponent) {
        if (hrRef == null || this.employeeList.some(e => e.value === hrRef)) return
        const inactive = parent.allEmployees.find(u => u.id === hrRef)
        const label = inactive ? `${inactive.name} (inactive)` : `${hrRef} (inactive)`
        this.employeeList = [new ListItem(hrRef, label), ...this.employeeList]
    }
}
